/// <amd-module name="Artifacts/Npm/components/NpmPackageAttributes" />
import { NpmPackagePeople } from "Artifacts/Npm/components/NpmPackagePeople";
import { NpmDependencyHelper } from "Artifacts/Npm/helpers/NpmDependencyHelper";
import { NpmCiConstants } from "Artifacts/Npm/NpmConstants";
import { NpmProtocolMetadata } from "Artifacts/Npm/NpmContracts";
import * as NpmRes from "Artifacts/Npm/Resources";
import { PackageDependencies } from "Artifacts/Package/components/package-details-common/PackageDependencies";
import {
  IProjectAttribute,
  PackageDevelopment,
} from "Artifacts/Package/components/package-details-common/PackageDevelopment";
import { PackageProvenance } from "Artifacts/Package/components/package-details-common/PackageProvenance";
import { PackageStats } from "Artifacts/Package/components/package-details-common/PackageStats";
import { PackageTags } from "Artifacts/Package/components/package-details-common/PackageTags";
import * as PackageRes from "Artifacts/Package/Resources";
import { IProtocolOverviewProps } from "Artifacts/Root/protocols/types";
import * as React from "react";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { useComponentContext } from "VSS/Platform/Layout";

const NpmPackageAttributes: React.FC<IProtocolOverviewProps> = ({ feed, pkg, version }) => {
  const { pageContext } = useComponentContext();
  const isProvenanceEnabled = isFeatureFlagEnabled(
    pageContext,
    "Packaging.Feed.WebAccess.Provenance",
    false,
  );
  const isMetricsEnabled = isFeatureFlagEnabled(
    pageContext,
    "Packaging.Feed.WebAccess.Metrics",
    false,
  );

  const npmMetadata: NpmProtocolMetadata | null =
    version.protocolMetadata && version.protocolMetadata.data
      ? (version.protocolMetadata.data as NpmProtocolMetadata)
      : null;
  const projectAttributes: IProjectAttribute[] = [];

  if (npmMetadata) {
    if (npmMetadata.homepage) {
      projectAttributes.push({
        iconClass: "Globe",
        text: NpmRes.NpmHomepage_Title,
        url: npmMetadata.homepage,
        onClickCiFeature: NpmCiConstants.HomepageLinkClicked,
      } as IProjectAttribute);
    }

    if (npmMetadata.bugs) {
      projectAttributes.push({
        iconClass: "FileBug",
        text: NpmRes.NpmBugs_Title,
        url: npmMetadata.bugs.url ? npmMetadata.bugs.url : "mailto:" + npmMetadata.bugs.email,
        onClickCiFeature: npmMetadata.bugs.url
          ? NpmCiConstants.BugUrlLinkClicked
          : NpmCiConstants.BugEmailLinkClicked,
      } as IProjectAttribute);
    }

    if (npmMetadata.license) {
      projectAttributes.push({
        iconClass: "Script",
        text: npmMetadata.license,
      } as IProjectAttribute);
    }

    if (npmMetadata.main) {
      projectAttributes.push({
        iconClass: "FileCode",
        text: npmMetadata.main,
      } as IProjectAttribute);
    }
  }

  return (
    <section className="package-overview-attributes" aria-label={PackageRes.PackageMetadata}>
      <div className="npm-overview-attributes-panel package-overview-attributes-panel">
        <PackageProvenance
          pageContext={pageContext}
          feed={feed}
          packageId={pkg.id}
          versionId={version.id}
          publishDate={new Date(version.publishDate)}
          isProvenanceEnabled={isProvenanceEnabled}
          isProvenanceSupported={true}
        />
        <NpmPackagePeople
          pageContext={pageContext}
          author={npmMetadata ? npmMetadata.author : null}
          contributors={npmMetadata ? npmMetadata.contributors : null}
        />
        <PackageDevelopment
          pageContext={pageContext}
          projectAttributes={projectAttributes}
          packageSummary={pkg}
          packageVersion={version}
          upstreamSources={feed.upstreamSources}
        />
        {isMetricsEnabled && (
          <PackageStats
            pageContext={pageContext}
            feed={feed}
            package={pkg}
            versionId={version.id}
            isMetricsEnabled={isMetricsEnabled}
          />
        )}
        <PackageDependencies
          pageContext={pageContext}
          feed={feed}
          packageSummary={pkg}
          packageVersion={version}
          dependencyHelperCallback={NpmDependencyHelper.formatGroupNames}
        />
        <PackageTags tags={version.tags} title={NpmRes.PackageTags_Title_Npm} />
        <PackageTags title={PackageRes.PackageDetails_VersionsTable_Views} tags={version.views} />
      </div>
    </section>
  );
};

export { NpmPackageAttributes };
